import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserBookings, logout } from '../store/userSlice';
import {
  Calendar,
  Clock,
  MapPin,
  Star,
  LogOut,
  Bell,
  User,
  History,
  Plus,
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';

const DashboardUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, bookings, loading } = useSelector((state) => state.user);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    dispatch(fetchUserBookings());
  }, [dispatch, user, navigate]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending_partner_acceptance: 'bg-yellow-100 text-yellow-800',
      accepted_by_partner: 'bg-green-100 text-green-800',
      rejected_by_partner: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled_by_user: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      pending_partner_acceptance: 'Waiting for Partner',
      accepted_by_partner: 'Confirmed',
      rejected_by_partner: 'Rejected',
      completed: 'Completed',
      cancelled_by_user: 'Cancelled',
    };
    return texts[status] || status;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-accent to-purple-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={user?.avatar || 'https://randomuser.me/api/portraits/men/32.jpg'}
              alt={user?.name}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h2 className="text-xl font-bold text-gray-800">{user?.name}</h2>
              <p className="text-sm text-gray-600">Customer</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-primary to-purple-600 text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all"
          >
            <Plus className="w-8 h-8 mb-2" />
            <h3 className="text-lg font-semibold mb-1">New Booking</h3>
            <p className="text-sm opacity-90">Book your next session</p>
          </motion.button>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <Calendar className="w-8 h-8 text-primary mb-2" />
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {bookings.filter((b) => b.status === 'accepted_by_partner').length}
            </h3>
            <p className="text-sm text-gray-600">Upcoming Sessions</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <History className="w-8 h-8 text-primary mb-2" />
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {bookings.filter((b) => b.status === 'completed').length}
            </h3>
            <p className="text-sm text-gray-600">Completed Sessions</p>
          </div>
        </div>

        {/* Bookings List */}
        <div className="bg-white rounded-3xl p-6 shadow-xl">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">My Bookings</h2>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-gray-200 h-32 rounded-xl" />
              ))}
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No bookings yet</p>
              <button
                onClick={() => navigate('/')}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Book Your First Session
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      {booking.partner && (
                        <img
                          src={booking.partner.image}
                          alt={booking.partner.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {booking.service}
                        </h3>
                        {booking.partner && (
                          <p className="text-gray-600">{booking.partner.name}</p>
                        )}
                        {booking.partner && (
                          <div className="flex items-center gap-1 mt-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm text-gray-600">
                              {booking.partner.rating}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                        booking.status
                      )}`}
                    >
                      {getStatusText(booking.status)}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{booking.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{booking.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{booking.location}</span>
                    </div>
                  </div>

                  {booking.status === 'completed' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <button className="text-primary hover:text-primary/80 font-medium text-sm">
                        Leave a Review
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardUser;
